import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { cherryQuotes, cherryFlirtyCheer } from "./cherryQuotes";

// Combine all quotes
const allQuotes = [...cherryQuotes, ...cherryFlirtyCheer];

// Music files from public folder
const musicTracks = ["/song1.mp3", "/song2.mp3", "/song3.mp3", "/song4.mp3"];

// Floating elements component
const FloatingElement = ({ type, delay = 0 }) => {
  const randomX = Math.random() * 100;
  const randomDuration = 20 + Math.random() * 10; // minimum 20 seconds

  const variants = {
    initial: {
      x: `${randomX}vw`,
      y: "110vh",
      rotate: 0,
    },
    animate: {
      y: "-10vh",
      x: [`${randomX}vw`, `${randomX + (Math.random() - 0.5) * 20}vw`],
      rotate: [0, 360],
      transition: {
        duration: randomDuration,
        repeat: Infinity,
        delay,
        ease: "linear",
        x: {
          duration: randomDuration,
          repeat: Infinity,
          ease: "easeInOut",
          repeatType: "reverse",
        },
      },
    },
  };

  const getElement = () => {
    switch (type) {
      case "heart":
        return (
          <div className="text-4xl md:text-5xl opacity-70">
            {Math.random() > 0.5 ? "💕" : "💖"}
          </div>
        );
      case "balloon":
        return <div className="text-4xl md:text-5xl opacity-60">🎈</div>;
      case "bubble":
        return (
          <div className="w-8 h-8 md:w-10 md:h-10 bg-white bg-opacity-20 rounded-full backdrop-blur-sm border border-white border-opacity-30"></div>
        );
      default:
        return null;
    }
  };

  return (
    <motion.div
      className="absolute pointer-events-none"
      variants={variants}
      initial="initial"
      animate="animate"
    >
      {getElement()}
    </motion.div>
  );
};

// Cat component with animations
const CuteCat = ({ showTooltip }) => {
  return (
    <motion.div
      className="relative"
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      exit={{ scale: 0, opacity: 0 }}
      transition={{ type: "spring", stiffness: 300, damping: 20 }}
    >
      <motion.div
        animate={{
          y: [0, -5, 0],
        }}
        transition={{
          duration: 3,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        className="relative"
      >
        {/* Cat emoji/character */}
        <div className="text-7xl md:text-8xl mb-4 relative">
          <motion.span
            animate={{
              rotateZ: [-5, 5, -5],
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="inline-block"
          >
            🐱
          </motion.span>

          {/* Blinking effect */}
          <motion.div
            className="absolute inset-0 flex items-center justify-center text-7xl md:text-8xl"
            animate={{ opacity: [1, 1, 0, 1, 1] }}
            transition={{
              duration: 4,
              repeat: Infinity,
              times: [0, 0.4, 0.45, 0.5, 1],
            }}
          >
            🐱
          </motion.div>
        </div>

        {/* Tooltip */}
        <AnimatePresence>
          {showTooltip && (
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.8 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.8 }}
              className="absolute -top-12 left-1/2 transform -translate-x-1/2 bg-white text-pink-600 px-4 py-2 rounded-full shadow-lg whitespace-nowrap font-bold text-sm"
            >
              <motion.span
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 1, repeat: Infinity }}
              >
                Click the button! 💕
              </motion.span>
              <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 rotate-45 w-3 h-3 bg-white"></div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
};

// Main App Component
export default function CherryLoveQuotesApp() {
  const [showQuote, setShowQuote] = useState(false);
  const [currentQuote, setCurrentQuote] = useState("");
  const [showTooltip, setShowTooltip] = useState(true);
  const [availableQuotes, setAvailableQuotes] = useState([...allQuotes]);
  const [usedQuotes, setUsedQuotes] = useState([]);
  const audioRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    // Preload audio
    audioRef.current = new Audio();

    // Cleanup
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const playRandomMusic = () => {
    if (audioRef.current) {
      const randomTrack =
        musicTracks[Math.floor(Math.random() * musicTracks.length)];
      audioRef.current.src = randomTrack;
      audioRef.current.volume = 0.3;
      audioRef.current
        .play()
        .catch((e) => console.log("Audio play failed:", e));
    }
  };

  const stopMusic = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  };

  const getRandomQuote = () => {
    let quotesPool = availableQuotes;
    let newUsedQuotes = [...usedQuotes];

    // If we've used all quotes, reset the pool
    if (quotesPool.length === 0) {
      quotesPool = [...allQuotes];
      newUsedQuotes = [];
      setAvailableQuotes([...allQuotes]);
      setUsedQuotes([]);
    }

    // Get random index from available quotes
    const randomIndex = Math.floor(Math.random() * quotesPool.length);
    const selectedQuote = quotesPool[randomIndex];

    // Remove selected quote from available and add to used
    const newAvailableQuotes = quotesPool.filter(
      (_, index) => index !== randomIndex
    );
    newUsedQuotes.push(selectedQuote);

    // Update state
    setAvailableQuotes(newAvailableQuotes);
    setUsedQuotes(newUsedQuotes);

    return selectedQuote;
  };

  const handleButtonClick = () => {
    // Hide tooltip after first click
    setShowTooltip(false);

    // Play click sound effect
    const clickSound = new Audio(
      "data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBiuBzvLTgjMGHm7A7OScTgwOVqzn77VeFAlFnt/yvmwhBiuBzvLTgjMHHW/A7OScTgwOVqzl77VeFAlFnt/yvmwhBiuBzvLTgjMHHW/A7OScTgwOVqzl77VeFAlFnt/yvmwhBiuBzvLTgjMHHW/A7OScTgwOVqzl77VeFAlFnt/yvmwhBiuBzvLTgjMHHW/A7OScTgwOVqzl77VeFAlFnt/yvmwhBi2Kz9uLMAAAA"
    );
    clickSound.volume = 0.2;
    clickSound.play().catch((e) => console.log("Click sound failed:", e));

    // Get random quote without repetition
    const randomQuote = getRandomQuote();
    setCurrentQuote(randomQuote);
    setShowQuote(true);

    // Play a random music track every time a quote shows
    playRandomMusic();

    // Hide quote after 20 seconds
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      setShowQuote(false);
      stopMusic();
    }, 20000); // changed from 10000 to 20000 ms
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-200 via-red-200 to-pink-300 overflow-hidden relative">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(6)].map((_, i) => (
          <FloatingElement key={`heart-${i}`} type="heart" delay={i * 2} />
        ))}
        {[...Array(4)].map((_, i) => (
          <FloatingElement key={`balloon-${i}`} type="balloon" delay={i * 3} />
        ))}
        {[...Array(8)].map((_, i) => (
          <FloatingElement key={`bubble-${i}`} type="bubble" delay={i * 1.5} />
        ))}
      </div>

      {/* Main Content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className="text-center">
          <AnimatePresence mode="wait">
            {!showQuote ? (
              <motion.div
                key="cat-button"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.5 }}
                className="flex flex-col items-center"
              >
                {/* Title */}
                <motion.h1
                  className="text-4xl md:text-5xl font-bold text-white mb-8 drop-shadow-lg"
                  animate={{
                    textShadow: [
                      "0 0 10px rgba(255,255,255,0.5)",
                      "0 0 20px rgba(255,182,193,0.8)",
                      "0 0 10px rgba(255,255,255,0.5)",
                    ],
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  For My Cherry 🍒
                </motion.h1>

                {/* Cat */}
                <CuteCat showTooltip={showTooltip} />

                {/* Button */}
                <motion.button
                  onClick={handleButtonClick}
                  className="px-8 py-4 bg-gradient-to-r from-pink-500 to-red-500 text-white font-bold text-xl rounded-full shadow-2xl hover:shadow-pink-500/50 transition-all duration-300"
                  whileHover={{
                    scale: 1.1,
                    boxShadow: "0 0 30px rgba(255,182,193,0.8)",
                  }}
                  whileTap={{ scale: 0.95 }}
                  animate={{
                    scale: [1, 1.05, 1],
                  }}
                  transition={{
                    scale: {
                      duration: 1.5,
                      repeat: Infinity,
                      ease: "easeInOut",
                    },
                  }}
                >
                  <span className="flex items-center gap-2">
                    Get Your Quote 💕
                  </span>
                </motion.button>

                {/* Quote counter */}
                <motion.div
                  className="mt-4 text-white text-sm opacity-70"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 0.7 }}
                  transition={{ delay: 1 }}
                >
                  {usedQuotes.length > 0 && (
                    <span>
                      Quotes shown: {usedQuotes.length} / {allQuotes.length}
                    </span>
                  )}
                </motion.div>
              </motion.div>
            ) : (
              <motion.div
                key="quote"
                initial={{ opacity: 0, scale: 0.5, y: 50 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.8, y: -50 }}
                transition={{
                  type: "spring",
                  stiffness: 200,
                  damping: 20,
                }}
                className="max-w-2xl mx-auto"
              >
                <motion.div
                  className="bg-white bg-opacity-90 backdrop-blur-md p-8 md:p-12 rounded-3xl shadow-2xl"
                  animate={{
                    boxShadow: [
                      "0 20px 40px rgba(0,0,0,0.1)",
                      "0 20px 60px rgba(255,182,193,0.4)",
                      "0 20px 40px rgba(0,0,0,0.1)",
                    ],
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: [0, 1.2, 1] }}
                    transition={{ duration: 0.5 }}
                    className="text-6xl mb-4"
                  >
                    💖
                  </motion.div>

                  <motion.p
                    className="text-2xl md:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-pink-600 to-red-600 leading-relaxed"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                    style={{
                      textShadow: "2px 2px 4px rgba(0,0,0,0.1)",
                      WebkitTextStroke: "0.5px rgba(255,182,193,0.3)",
                    }}
                  >
                    {currentQuote}
                  </motion.p>

                  <motion.div
                    className="mt-6 flex justify-center gap-2"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                  >
                    {[...Array(5)].map((_, i) => (
                      <motion.span
                        key={i}
                        animate={{
                          scale: [1, 1.2, 1],
                          rotate: [0, 10, -10, 0],
                        }}
                        transition={{
                          duration: 1,
                          delay: i * 0.1,
                          repeat: Infinity,
                          repeatDelay: 1,
                        }}
                        className="text-2xl"
                      >
                        💕
                      </motion.span>
                    ))}
                  </motion.div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <div className="font-bold flex justify-center items-center text-center">
        Made with{" "}
        <motion.span
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 10, -10, 0],
          }}
          transition={{
            duration: 1,

            repeat: Infinity,
            repeatDelay: 1,
          }}
          className="text-2xl"
        >
          💕
        </motion.span>{" "}
        by gappu
      </div>
    </div>
  );
}
